// src/features/open_figis/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  CheckboxField,
  DatePickerField,
  InputField,
  TextareaField,
} from "@/features/FormCard/Fields";

import { useEditForm } from "@/features/Edit/hooks";

import {
  dateSchema,
  parseToDateValue,
  parseYes,
} from "@/features/FormCard/utils";

const FormSchema = z.object({
  unique_id: z.string().nullable().optional(),
  figi: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  ticker: z.string().nullable().optional(),
  exchange_code: z.string().nullable().optional(),
  composite: z.string().nullable().optional(),
  security_type: z.string().nullable().optional(),
  security_type2: z.string().nullable().optional(),
  security_description: z.string().nullable().optional(),
  market_sector: z.string().nullable().optional(),
  figi_share_class: z.string().nullable().optional(),
  fut_opt: z.string().nullable().optional(),
  is_perpetual: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  maturity: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  coupon: z.coerce.number().optional().nullable(),
});

export function OpenFigisEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const { form } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <CardHeader className="text-lg font-bold text-gray-700">
              OpenFigis {id}
            </CardHeader>
            <Divider />
            <CardBody className="flex flex-col gap-4">
              {/* Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  name="unique_id"
                  label={t(`${table_name}.columns.unique_id`, {
                    ns: "tables",
                    defaultValue: "Unique ID",
                  })}
                  control={form.control}
                />
                <InputField
                  name="figi"
                  label={t(`${table_name}.columns.figi`, {
                    ns: "tables",
                    defaultValue: "FIGI",
                  })}
                  control={form.control}
                />
              </div>
              {/* Row 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  name="name"
                  label={t(`${table_name}.columns.name`, {
                    ns: "tables",
                    defaultValue: "Name",
                  })}
                  control={form.control}
                />
                <InputField
                  name="ticker"
                  label={t(`${table_name}.columns.ticker`, {
                    ns: "tables",
                    defaultValue: "Ticker",
                  })}
                  control={form.control}
                />
              </div>
              {/* Row 3 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  name="exchange_code"
                  label={t(`${table_name}.columns.exchange_code`, {
                    ns: "tables",
                    defaultValue: "Exchange Code",
                  })}
                  control={form.control}
                />
                <InputField
                  name="composite"
                  label={t(`${table_name}.columns.composite`, {
                    ns: "tables",
                    defaultValue: "Composite",
                  })}
                  control={form.control}
                />
              </div>
              {/* Row 4 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  name="security_type"
                  label={t(`${table_name}.columns.security_type`, {
                    ns: "tables",
                    defaultValue: "Security Type",
                  })}
                  control={form.control}
                />
                <InputField
                  name="security_type2"
                  label={t(`${table_name}.columns.security_type2`, {
                    ns: "tables",
                    defaultValue: "Security Type 2",
                  })}
                  control={form.control}
                />
              </div>
              {/* Full-width description */}
              <TextareaField
                name="security_description"
                label={t(`${table_name}.columns.security_description`, {
                  ns: "tables",
                  defaultValue: "Security Description",
                })}
                control={form.control}
              />
              {/* Row 5 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  name="market_sector"
                  label={t(`${table_name}.columns.market_sector`, {
                    ns: "tables",
                    defaultValue: "Market Sector",
                  })}
                  control={form.control}
                />
                <InputField
                  name="figi_share_class"
                  label={t(`${table_name}.columns.figi_share_class`, {
                    ns: "tables",
                    defaultValue: "FIGI Share Class",
                  })}
                  control={form.control}
                />
              </div>
              {/* Row 6 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  name="fut_opt"
                  label={t(`${table_name}.columns.fut_opt`, {
                    ns: "tables",
                    defaultValue: "Fut Opt",
                  })}
                  control={form.control}
                />
                <CheckboxField
                  name="is_perpetual"
                  label={t(`${table_name}.columns.is_perpetual`, {
                    ns: "tables",
                    defaultValue: "Is Perpetual",
                  })}
                  control={form.control}
                />
              </div>
              {/* Row 7 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DatePickerField
                  name="maturity"
                  label={t(`${table_name}.columns.maturity`, {
                    ns: "tables",
                    defaultValue: "Maturity",
                  })}
                  control={form.control}
                />
                <InputField
                  name="coupon"
                  label={t(`${table_name}.columns.coupon`, {
                    ns: "tables",
                    defaultValue: "Coupon",
                  })}
                  type="number"
                  control={form.control}
                />
              </div>
            </CardBody>
          </Form>
        </Card>
      </div>
    </div>
  );
}
